import Peer, {DataConnection} from "peerjs";
import {message} from "antd";
import { PEER_HOSTNAME, PEER_PORT, PEER_SECURE, PEER_URL } from "../../const/Constants";

// Files travel over a PeerJS "raw" connection as JSON strings, the same protocol as the
// mobile app (MeetMobile src/services/p2p.js):
//   {t: "start", id, name, mime, size} -> {t: "chunk", id, data: base64}... -> {t: "end", id}
//   {t: "abort", id} when the sender gives up.
// Plain strings work identically in every PeerJS version and in React Native,
// unlike PeerJS's binary format (needs browser Blob support).
const CHUNK_BYTES = 32 * 1024 // ~43 KB as base64, well under data-channel message limits
const MAX_BUFFERED = 1024 * 1024 // wait while more than this is queued on the channel

export interface ReceivedFile {
    name: string
    type: string
    blob: Blob
}
export interface TransferProgress {
    direction: "send" | "receive"
    name: string
    percent: number
}

const sleep = (ms: number) => new Promise(r => setTimeout(r, ms))

const toBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer)
    let binary = ""
    for (let i = 0; i < bytes.length; i += 0x8000) {
        binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000)))
    }
    return btoa(binary)
}

const fromBase64 = (data: string): Uint8Array => {
    const binary = atob(data)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    return bytes
}

let peer: Peer | undefined
// keyed by username, PeerJS ids are only used when talking to PeerJS itself
let connectionMap: Map<string, DataConnection> = new Map<string, DataConnection>()
// usernames this user agreed to exchange files with; everyone else is refused
let allowedPeers: Set<string> = new Set<string>()
// the open transfer panel shows progress of files being received too
let progressListener: ((p: TransferProgress) => void) | undefined

// PeerJS ids may only contain letters and digits joined by single "-", "_" or " ",
// but usernames can contain "." (e.g. the ones generated from an email at signup).
// Hex-encoding gives every username exactly one valid id that decodes back.
const PEER_ID_PREFIX = "u"

export const toPeerId = (username: string): string =>
    PEER_ID_PREFIX + Array.from(new TextEncoder().encode(username))
        .map(byte => byte.toString(16).padStart(2, "0"))
        .join("")

export const fromPeerId = (peerId: string): string => {
    if (!/^u(?:[0-9a-f]{2})+$/.test(peerId)) return peerId
    const bytes = peerId.slice(PEER_ID_PREFIX.length).match(/../g)!.map(hex => parseInt(hex, 16))
    return new TextDecoder().decode(new Uint8Array(bytes))
}

export const PeerConnection = {
    getPeer: () => peer,
    allowPeer: (username: string) => { allowedPeers.add(username) },
    setProgressListener: (listener?: (p: TransferProgress) => void) => { progressListener = listener },
    disallowPeer: (username: string) => { allowedPeers.delete(username) },
    startPeerSession: () => new Promise<string>((resolve, reject) => {
        try {
            let user = "" + localStorage.getItem("user")
            let opened = false
            peer = new Peer(toPeerId(user), {host: PEER_HOSTNAME, port: +PEER_PORT, secure: PEER_SECURE, path: PEER_URL})
            peer.on('open', (id) => {
                opened = true
                resolve(fromPeerId(id))
            }).on('error', (err) => {
                console.error("PeerJS error:", err.type, err.message)
                message.error("Սխալմունք")
                // without this a failed start leaves the transfer panel waiting forever
                if (!opened) reject(err)
            })
        } catch (err) {
            reject(err)
        }
    }),
    closePeerSession: (id) => new Promise<void>((resolve, reject) => {
        try {
            if (peer) {
                peer.destroy()
                peer = undefined
                connectionMap.delete(id)
                allowedPeers.clear()
            }
            resolve()
        } catch (err) {
            reject(err)
        }
    }),
    connectPeer: (id: string) => new Promise<void>((resolve, reject) => {
        if (!peer) {
            reject(new Error(""))
            return
        }
        if (connectionMap.has(id)) {
            reject(new Error(""))
            return
        }
        try {
            let conn = peer.connect(toPeerId(id), {reliable: true, serialization: "raw"})
            if (!conn) {
                reject(new Error(""))
            } else {
                conn.on('open', function() {
                    connectionMap.set(id, conn)
                    resolve()
                }).on('error', function(err) {
                    reject(err)
                })
            }
        } catch (err) {
            reject(err)
        }
    }),
    onIncomingConnection: (callback: (conn: DataConnection, username: string) => void) => {
        peer?.on('connection', function (conn) {
            const username = fromPeerId(conn.peer)
            // peer ids are predictable, so anyone could try to connect directly:
            // only people whose request was accepted in the app get through
            if (!allowedPeers.has(username)) {
                conn.on('open', () => conn.close())
                return
            }
            connectionMap.set(username, conn)
            callback(conn, username)
        });
    },
    onConnectionDisconnected: (id: string, callback: () => void) => {
        if (!peer) {
            throw new Error("")
        }
        if (!connectionMap.has(id)) {
            throw new Error("")
        }
        let conn = connectionMap.get(id);
        if (conn) {
            conn.on('close', function () {
                connectionMap.delete(id)
                callback()
            });
        }
    },
    // Sends one file in chunks; onProgress gets 0..100
    sendFile: async (id: string, file: File, onProgress?: (p: TransferProgress) => void): Promise<void> => {
        const conn = connectionMap.get(id)
        if (!conn || !conn.open) throw new Error("not connected")
        const transferId = Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
        const send = (message: object) => conn.send(JSON.stringify(message))
        // don't pile the whole file into memory: wait while the channel is still full
        const drain = async () => {
            while (conn.open && ((conn.dataChannel?.bufferedAmount || 0) > MAX_BUFFERED || conn.bufferSize > 0)) {
                await sleep(25)
            }
        }
        send({t: "start", id: transferId, name: file.name, mime: file.type || "application/octet-stream", size: file.size})
        let sent = 0
        try {
            while (sent < file.size) {
                if (!conn.open) throw new Error("connection closed")
                const chunk = await file.slice(sent, sent + CHUNK_BYTES).arrayBuffer()
                await drain()
                send({t: "chunk", id: transferId, data: toBase64(chunk)})
                sent += chunk.byteLength
                onProgress?.({direction: "send", name: file.name, percent: Math.round(sent / Math.max(file.size, 1) * 100)})
            }
            await drain()
            send({t: "end", id: transferId})
        } catch (err) {
            try { send({t: "abort", id: transferId}) } catch (e) {}
            throw err
        }
    },
    // Rebuilds incoming files from the chunk messages
    onConnectionReceiveData: (id: string, callback: (f: ReceivedFile) => void, onProgress = (p: TransferProgress) => progressListener?.(p)) => {
        if (!peer) {
            throw new Error("")
        }
        const conn = connectionMap.get(id)
        if (!conn) {
            throw new Error("")
        }
        const incoming: Record<string, {name: string, type: string, size: number, received: number, parts: Uint8Array[]}> = {}
        conn.on('data', function (raw) {
            let msg: any
            try {
                msg = typeof raw === "string" ? JSON.parse(raw) : raw
            } catch (e) {
                return
            }
            if (!msg || !msg.t || !msg.id) return
            const transfer = incoming[msg.id]
            if (msg.t === "start") {
                incoming[msg.id] = {name: String(msg.name || "file").slice(0, 200), type: msg.mime || "application/octet-stream", size: msg.size || 0, received: 0, parts: []}
                onProgress?.({direction: "receive", name: incoming[msg.id].name, percent: 0})
            } else if (msg.t === "chunk" && transfer) {
                const bytes = fromBase64(msg.data)
                transfer.parts.push(bytes)
                transfer.received += bytes.length
                onProgress?.({direction: "receive", name: transfer.name, percent: transfer.size ? Math.min(99, Math.round(transfer.received / transfer.size * 100)) : 0})
            } else if (msg.t === "end" && transfer) {
                delete incoming[msg.id]
                onProgress?.({direction: "receive", name: transfer.name, percent: 100})
                callback({name: transfer.name, type: transfer.type, blob: new Blob(transfer.parts, {type: transfer.type})})
            } else if (msg.t === "abort" && transfer) {
                delete incoming[msg.id]
            }
        })
    }

}